import TextInput from '~/components/TextInput';
import Toggle from '~/components/Toggle';
import Select from '~/components/Select';
import FileUploadImmediate from '~/components/FileUploadImmediate';
import useBusinessNavigate from '~/lib/useBusinessNavigate';
import usePermissions from '~/lib/usePermissions';
import { formatCurrency } from '~/lib/CurrencyHelpers';
import { useCustomQuestion } from '~/lib/CustomQuestions/CustomQuestionRegistry';
import type {
    CustomQuestionField,
    CustomQuestionDefinition,
} from '~/lib/CustomQuestions/CustomQuestionApi';
import type { QuestionSettings } from '~/lib/Questions/Questions.types';

type EditCustomQuestionFormProps = {
    settings: QuestionSettings;
    setSettings: (settings: QuestionSettings) => void;
    /**
     * The custom question editor's unsaved draft. Only ever published code is
     *  in the registry, so the editor hands its own schema in to show the
     *  author what the business will be asked
     */
    definitionOverride?: CustomQuestionDefinition | null;
};

/**
 * The settings panel for a custom question.
 *
 * The question declared what it wanted to be asked, as a list of fields, and
 *  this renders them with the same controls every other question's panel uses.
 *  A field type we don't know about is skipped and said so rather than drawn
 *  as something it isn't.
 */
const EditCustomQuestionForm = ({
    settings,
    setSettings,
    definitionOverride,
}: EditCustomQuestionFormProps) => {
    const registered = useCustomQuestion(settings?.customQuestionId);
    const definition = definitionOverride ?? registered;
    const navigate = useBusinessNavigate();
    const { isFullAccess } = usePermissions();

    if (!definition) {
        return (
            <div className="mt-4 text-gray-500 text-sm">
                The custom question this uses could not be found. It may have
                been deleted.
            </div>
        );
    }

    const fields = definition.fields || [];

    function set(key: string, value: any) {
        setSettings({ ...settings, [key]: value });
    }

    return (
        <div className="mt-4">
            {fields.map((field, idx) => (
                <Field
                    key={field.key || `note-${idx}`}
                    field={field}
                    value={settings?.[field.key]}
                    onChange={value => set(field.key, value)}
                />
            ))}

            {!fields.length && (
                <div className="text-gray-500 text-sm">
                    This question has no settings.
                </div>
            )}

            {isFullAccess() && !definitionOverride && (
                <button
                    type="button"
                    className="mt-6 text-sm link"
                    onClick={() =>
                        navigate(`/custom-questions/${definition.id}`)
                    }
                >
                    Edit the code for {definition.name}
                </button>
            )}
        </div>
    );
};

const Field = ({
    field,
    value,
    onChange,
}: {
    field: CustomQuestionField;
    value: any;
    onChange: (value: any) => void;
}) => {
    const current = value ?? field.default ?? '';

    switch (field.type) {
        case 'note':
            return (
                <div className="text-gray-500 text-sm my-3">{field.text}</div>
            );

        case 'toggle':
            return (
                <div className="flex justify-between items-center mt-3">
                    <div>
                        <div className="text-sm">{field.label}</div>
                        {!!field.description && (
                            <div className="text-gray-500 text-sm">
                                {field.description}
                            </div>
                        )}
                    </div>
                    <Toggle
                        enabled={!!(value ?? field.default)}
                        setEnabled={onChange}
                    />
                </div>
            );

        case 'number':
            return (
                <TextInput
                    className="mt-3"
                    label={field.label}
                    type="number"
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    inputClassName="w-full"
                    value={`${current}`}
                    onChange={next =>
                        onChange(next === '' ? null : parseFloat(next))
                    }
                />
            );

        case 'select':
            return (
                <Select
                    className="mt-3"
                    label={field.label}
                    options={field.options || []}
                    value={current}
                    onChange={onChange}
                />
            );

        case 'longtext':
            return (
                <div className="mt-3">
                    <div className="input-label">{field.label}</div>
                    <textarea
                        className="w-full min-h-24"
                        value={current}
                        onChange={e => onChange(e.target.value)}
                    />
                    {!!field.description && (
                        <div className="text-gray-500 text-sm mt-1">
                            {field.description}
                        </div>
                    )}
                </div>
            );

        case 'list':
            return (
                <div className="mt-3">
                    <div className="input-label">{field.label}</div>
                    <textarea
                        className="w-full min-h-24"
                        value={(Array.isArray(value) ? value : []).join('\n')}
                        onChange={e => onChange(e.target.value.split('\n'))}
                    />
                    <div className="text-gray-500 text-sm mt-1">
                        {field.description || 'One per line'}
                    </div>
                </div>
            );

        case 'color':
            return (
                <div className="mt-3 flex items-center justify-between">
                    <div className="text-sm">{field.label}</div>
                    <input
                        type="color"
                        value={current || '#000000'}
                        onChange={e => onChange(e.target.value)}
                    />
                </div>
            );

        case 'image':
            return (
                <div className="mt-3">
                    <div className="input-label">{field.label}</div>
                    {!!current && (
                        <img
                            src={current}
                            alt=""
                            className="max-h-24 rounded mb-2"
                        />
                    )}
                    <div className="flex items-center gap-x-3">
                        <FileUploadImmediate onChange={onChange}>
                            <span className="btn-inverse-pill">
                                {current ? 'Replace' : 'Upload'}
                            </span>
                        </FileUploadImmediate>
                        {!!current && (
                            <button
                                type="button"
                                className="text-sm text-gray-500"
                                onClick={() => onChange('')}
                            >
                                Remove
                            </button>
                        )}
                    </div>
                </div>
            );

        case 'priceList':
            return (
                <PriceList
                    field={field}
                    value={Array.isArray(value) ? value : field.default || []}
                    onChange={onChange}
                />
            );

        case 'text':
        default:
            return (
                <TextInput
                    className="mt-3"
                    label={field.label}
                    inputClassName="w-full"
                    maxLength={field.maxLength}
                    value={current}
                    onChange={onChange}
                />
            );
    }
};

/**
 * What a question is allowed to charge for.
 *
 * The keys here are the only thing breely.setCharges may name, and the prices
 *  are looked up from these settings again on the server when the answer is
 *  actually charged for - so what a client's browser posts can't decide what
 *  they pay.
 */
const PriceList = ({
    field,
    value,
    onChange,
}: {
    field: CustomQuestionField;
    value: Array<Record<string, any>>;
    onChange: (value: Array<Record<string, any>>) => void;
}) => {
    const items = Array.isArray(value) ? value : [];

    function update(idx: number, changes: Record<string, any>) {
        onChange(
            items.map((item, i) =>
                i === idx ? { ...item, ...changes } : item,
            ),
        );
    }

    /**
     * A key the question's code hasn't seen can never be charged for, so a new
     *  row gets one made from its name rather than typed - and keys already in
     *  use are left alone
     */
    function addItem() {
        const taken = new Set(items.map(item => item.key));
        let key = `item_${items.length + 1}`;

        for (let n = items.length + 1; taken.has(key); n++) {
            key = `item_${n}`;
        }

        onChange([...items, { key, name: '', price: 0 }]);
    }

    return (
        <div className="mt-4">
            <div className="panel-label">{field.label}</div>
            <div className="panel pl-4">
                {items.map((item, idx) => (
                    <div
                        key={idx}
                        className={`flex items-center justify-between py-3 pr-4 ${idx > 0 ? 'border-t' : ''}`}
                    >
                        <div className="flex-1 grid grid-cols-2 gap-x-3">
                            <div>
                                <TextInput
                                    placeholder="Name"
                                    inputClassName="w-full"
                                    value={item.name ?? ''}
                                    onChange={next =>
                                        update(idx, { name: next })
                                    }
                                />
                                {/* The question's code charges by this key, so
                                    it is shown rather than edited - renaming it
                                    would quietly stop the charge happening */}
                                <div className="font-mono text-xs text-gray-500 mt-1 ml-4">
                                    {item.key}
                                </div>
                            </div>
                            <TextInput
                                placeholder="0.00"
                                type="number"
                                min={0}
                                step="0.01"
                                inputClassName="w-full"
                                value={`${item.price ?? ''}`}
                                onChange={next =>
                                    update(idx, {
                                        price:
                                            next === '' ? 0 : parseFloat(next),
                                    })
                                }
                            />
                        </div>
                        <button
                            type="button"
                            className="ml-3 text-sm text-gray-500"
                            onClick={() =>
                                onChange(items.filter((_, i) => i !== idx))
                            }
                        >
                            Remove
                        </button>
                    </div>
                ))}

                <div className="py-3 pr-4 border-t flex items-center justify-between">
                    <button
                        type="button"
                        className="btn-inverse-pill"
                        onClick={addItem}
                    >
                        Add an item
                    </button>
                    {items.length > 0 && (
                        <div className="text-gray-500 text-sm">
                            {items.length} item
                            {items.length === 1 ? '' : 's'}, up to{' '}
                            {formatCurrency(
                                items.reduce(
                                    (total, item) =>
                                        total + (parseFloat(item.price) || 0),
                                    0,
                                ),
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="text-gray-500 text-sm mt-1 ml-4">
                The question charges for these by the key shown under each name.
                Prices are always taken from here, never from the question's own
                code.
            </div>
        </div>
    );
};

export default EditCustomQuestionForm;
